import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle, Calendar, Clock, CheckSquare, Brain, Loader2,
  Send, Download, ExternalLink,
} from 'lucide-react'
import { apiClient } from '../services/api'
import { type Task } from '../services/tasks'
import { EmptyState } from '../components/ui/EmptyState'
import { PriorityBadge } from '../components/ui/PriorityBadge'
import { DateDisplay } from '../components/ui/DateDisplay'
import { InsightCard } from '../components/ui/InsightCard'
import { toast } from 'sonner'
import type { ApiErrorLike } from '../utils/apiError'

interface Section {
  id: string
  label: string
  icon: typeof AlertCircle
  tasks: Task[]
  emptyText: string
  highlight?: boolean
  accent: string
}

interface AIInsight {
  id: number
  type: string
  title: string
  content?: string
  reasoning?: string
  suggested_actions?: string[]
  confidence?: number
  urgency?: string
  feedback_score?: number | null
}

function today() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

function isSameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString()
}

function isThisWeek(date: Date) {
  const now = new Date()
  const weekStart = new Date(now)
  weekStart.setDate(now.getDate() - now.getDay())
  weekStart.setHours(0, 0, 0, 0)
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekStart.getDate() + 7)
  return date >= weekStart && date < weekEnd
}

function generateICS(tasks: Task[]): string {
  const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Karyva//Tasks//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ]
  for (const task of tasks) {
    const uid = `task-${task.id}@karyva`
    const due = task.due_date
      ? new Date(task.due_date).toISOString().replace(/[-:]/g, '').split('T')[0]
      : new Date().toISOString().replace(/[-:]/g, '').split('T')[0]
    lines.push(
      'BEGIN:VTODO',
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      `SUMMARY:${task.title}`,
      `DESCRIPTION:${task.description || ''}`,
      `DUE;VALUE=DATE:${due}`,
      `PRIORITY:${task.priority === 'urgent' ? 1 : task.priority === 'high' ? 3 : task.priority === 'medium' ? 5 : 9}`,
      `STATUS:${task.status === 'done' ? 'COMPLETED' : task.status === 'in_progress' ? 'IN-PROCESS' : 'NEEDS-ACTION'}`,
      'END:VTODO',
    )
  }
  lines.push('END:VCALENDAR')
  return lines.join('\r\n')
}

export default function MyDay() {
  const navigate = useNavigate()
  const [tasks, setTasks] = useState<Task[]>([])
  const [insights, setInsights] = useState<AIInsight[]>([])
  const [loading, setLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState<number | null>(null)
  const [exportingSlack, setExportingSlack] = useState(false)

  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        const [taskRes, insightRes] = await Promise.all([
          apiClient.get<Task[]>('/api/v1/tasks', { params: { assigned_to_me: true, limit: 200 } }),
          apiClient.get<AIInsight[]>('/api/v1/ai/insights', { params: { target_role: 'all', limit: 5 } }),
        ])
        setTasks(taskRes.data ?? [])
        setInsights(insightRes.data ?? [])
      } catch {
        // Tasks may 404 on first run
      }
      setLoading(false)
    }
    load()
  }, [])

  const todayDate = today()
  const overdue = tasks.filter(t => t.due_date && new Date(t.due_date) < todayDate && t.status !== 'done')
  const dueToday = tasks.filter(t => t.due_date && isSameDay(new Date(t.due_date), todayDate) && t.status !== 'done')
  const thisWeek = tasks.filter(t => {
    if (!t.due_date) return false
    const d = new Date(t.due_date)
    return isThisWeek(d) && !isSameDay(d, todayDate) && d >= todayDate && t.status !== 'done'
  })

  const sections: Section[] = [
    { id: 'overdue', label: 'Overdue', icon: AlertCircle, tasks: overdue, emptyText: 'No overdue tasks.', highlight: true, accent: 'border-red-500/40 bg-red-500/5' },
    { id: 'today', label: 'Due Today', icon: Calendar, tasks: dueToday, emptyText: 'Nothing due today.', accent: 'border-[#DAA520]/40 bg-[#DAA520]/5' },
    { id: 'week', label: 'This Week', icon: Clock, tasks: thisWeek, emptyText: 'Nothing else due this week.', accent: 'border-[#486581]/40 bg-[#486581]/5' },
  ]

  async function toggleStatus(task: Task) {
    const next = task.status === 'done' ? 'todo' : 'done'
    setUpdatingId(task.id)
    try {
      await apiClient.put(`/api/v1/tasks/${task.id}`, { status: next })
      setTasks(ts => ts.map(t => t.id === task.id ? { ...t, status: next } : t))
    } finally {
      setUpdatingId(null)
    }
  }

  async function handleInsightFeedback(id: number, score: 1 | -1) {
    await apiClient.post(`/api/v1/ai/insights/${id}/feedback`, { score })
  }

  async function exportToSlack() {
    const pending = [...overdue, ...dueToday]
    if (pending.length === 0) {
      toast.info('No overdue or today tasks to export')
      return
    }
    setExportingSlack(true)
    try {
      await apiClient.post('/api/v1/integrations/slack/export-tasks', {
        task_ids: pending.map(t => t.id),
      })
      toast.success('Tasks posted to Slack')
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err.response?.data?.detail || 'Failed to post to Slack. Check your Slack integration.')
    } finally {
      setExportingSlack(false)
    }
  }

  function exportToOutlook() {
    const allPending = [...overdue, ...dueToday, ...thisWeek]
    if (allPending.length === 0) {
      toast.info('No tasks to export')
      return
    }
    const ics = generateICS(allPending)
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `karyva-my-day-${new Date().toISOString().split('T')[0]}.ics`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Calendar file downloaded — open with Outlook to import')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-[#486581]" />
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Page header — matches app gradient style */}
      <div className="bg-gradient-to-r from-[#102a43] via-[#486581] to-[#102a43] rounded-xl p-4 sm:p-6 text-white shadow-lg border border-[#829ab1]/20">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl mb-1">My Day</h1>
            <p className="text-[#bcccdc] text-sm">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 flex-shrink-0">
            <button
              onClick={exportToSlack}
              disabled={exportingSlack}
              className="flex items-center gap-1.5 min-h-[44px] px-3 py-1.5 bg-[#4A154B] hover:bg-[#611f69] border border-[#829ab1]/30 rounded-lg text-sm transition-colors disabled:opacity-50"
              title="Post today's tasks to Slack"
            >
              {exportingSlack
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Send className="w-3.5 h-3.5" />}
              Slack
            </button>
            <button
              onClick={exportToOutlook}
              className="flex items-center gap-1.5 min-h-[44px] px-3 py-1.5 bg-[#0072C6] hover:bg-[#005a9e] border border-[#829ab1]/30 rounded-lg text-sm transition-colors"
              title="Export tasks as .ics for Outlook"
            >
              <Download className="w-3.5 h-3.5" />
              Outlook
            </button>
          </div>
        </div>

        {/* Summary stat row */}
        <div className="grid grid-cols-3 gap-3 mt-5">
          {[
            { label: 'Overdue', value: overdue.length, color: overdue.length > 0 ? 'text-red-400' : 'text-[#bcccdc]' },
            { label: 'Due Today', value: dueToday.length, color: dueToday.length > 0 ? 'text-[#DAA520]' : 'text-[#bcccdc]' },
            { label: 'This Week', value: thisWeek.length, color: 'text-[#bcccdc]' },
          ].map(stat => (
            <div key={stat.label} className="bg-[#102a43]/60 rounded-lg px-4 py-3 border border-[#829ab1]/20 text-center">
              <div className={`text-2xl font-semibold ${stat.color}`}>{stat.value}</div>
              <div className="text-xs text-[#829ab1] mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Insights strip */}
      {insights.length > 0 && (
        <section aria-labelledby="insights-heading">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-4 h-4 text-[#DAA520]" aria-hidden="true" />
            <h2 id="insights-heading" className="text-sm font-semibold text-[#2A3B4C]">AI Insights</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {insights.map(i => (
              <InsightCard key={i.id} insight={i} onFeedback={handleInsightFeedback} enableNav />
            ))}
          </div>
        </section>
      )}

      {/* Task sections */}
      {sections.map(section => (
        <section key={section.id} aria-labelledby={`${section.id}-heading`}>
          <div className="flex items-center gap-2 mb-3">
            <section.icon
              className={`w-4 h-4 ${section.highlight ? 'text-red-500' : 'text-[#486581]'}`}
              aria-hidden="true"
            />
            <h2 id={`${section.id}-heading`} className="text-sm font-semibold text-[#2A3B4C]">
              {section.label}
              {section.tasks.length > 0 && (
                <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded-full ${
                  section.highlight
                    ? 'bg-red-100 text-red-700'
                    : section.id === 'today'
                      ? 'bg-[#DAA520]/15 text-[#8B6914]'
                      : 'bg-[#486581]/10 text-[#486581]'
                }`}>
                  {section.tasks.length}
                </span>
              )}
            </h2>
          </div>

          {section.tasks.length === 0 ? (
            <p className="text-sm text-[#556B7C] pl-6">{section.emptyText}</p>
          ) : (
            <ul className={`space-y-2 rounded-xl border p-3 ${section.accent}`} role="list">
              {section.tasks.map(task => (
                <li
                  key={task.id}
                  className="flex items-center gap-3 rounded-lg border border-[#C4CDD5] bg-white px-4 py-3 hover:bg-[#f0f4f8] transition-colors shadow-sm"
                >
                  <button
                    onClick={() => toggleStatus(task)}
                    disabled={updatingId === task.id}
                    className="flex-shrink-0 w-5 h-5 rounded border-2 border-[#8B9BAB] flex items-center justify-center hover:border-[#486581] transition-colors"
                    aria-label={task.status === 'done' ? 'Mark as incomplete' : 'Mark as complete'}
                  >
                    {updatingId === task.id
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : task.status === 'done' && <CheckSquare className="w-3 h-3 text-[#486581]" />
                    }
                  </button>

                  <button
                    onClick={() => navigate(`/task/${task.id}`)}
                    className={`flex-1 text-sm text-left ${
                      task.status === 'done'
                        ? 'line-through text-[#8B9BAB]'
                        : 'text-[#2A3B4C]'
                    } hover:text-[#486581] transition-colors`}
                  >
                    {task.title}
                  </button>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <PriorityBadge priority={task.priority} />
                    {task.due_date && (
                      <DateDisplay
                        date={task.due_date}
                        className={section.id === 'overdue' ? 'text-red-600 font-medium' : ''}
                      />
                    )}
                    <button
                      onClick={() => navigate(`/task/${task.id}`)}
                      className="text-[#8B9BAB] hover:text-[#486581] transition-colors"
                      aria-label="Open task"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}

      {tasks.length === 0 && (
        <EmptyState
          icon={CheckSquare}
          headline="You're all caught up"
          subtext="No tasks assigned to you. Tasks will appear here once they're assigned to you in a project."
          ctaLabel="View all projects"
          onCta={() => navigate('/project')}
        />
      )}
    </div>
  )
}
